import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success

  const submit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    try {
      const { data } = await api.post("/subscribers", { email });
      setState("success");
      trackEvent("newsletter_subscribe", { already_subscribed: !!data.already_subscribed });
      if (data.already_subscribed) {
        toast.info("You're already subscribed — thanks!");
      } else {
        toast.success("Welcome — check your inbox for a confirmation.");
      }
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed. Try again.");
      setState("idle");
    }
  };

  return (
    <section className="py-24 md:py-32 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.3em] text-[#0891B2]">
            <Mail size={12} /> Newsletter
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05] text-slate-900 dark:text-white mb-4">
            The <span className="text-[#0891B2]">Pipeline Brief</span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            A weekly 5-minute read on B2B demand engineering: pipeline benchmarks, ABM plays,
            content that converts, and the operator moves we're seeing across categories.
          </p>

          <form
            onSubmit={submit}
            data-testid="newsletter-form"
            className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3"
          >
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={state === "submitting"}
              className="flex-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm px-4 py-3 outline-none focus:border-[#007AFF] transition-colors placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={state === "submitting"}
              className="inline-flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#0056d3] disabled:opacity-60 text-white text-sm font-medium px-6 py-3 shadow-[0_4px_20px_rgba(0,122,255,0.25)] hover:shadow-[0_6px_28px_rgba(0,122,255,0.4)] transition-colors whitespace-nowrap"
            >
              {state === "submitting" ? "Subscribing…" : state === "success" ? (
                <>Subscribed <CheckCircle2 size={16} /></>
              ) : (
                <>Subscribe <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500 dark:text-slate-500">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#0891B2]" /> Weekly</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#0891B2]" /> No spam</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#0891B2]" /> Unsubscribe anytime</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
